import React, { useState } from 'react';
import PatientFields from './PatientFields';
import CustomPlanFields from '../../CustomPlanFields';
import { getPlanFieldConfig, planHasCustomFields } from '../../../helpers/planFieldConfigs';
import { CASE_TYPES, CHANNELS } from '../../../constants/formConstants';

/**
 * Error notification with collapsible details
 * Shows "Case creation failed" with expandable raw error details
 */
const ErrorNotification = ({ message, onDismiss }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasDetails = message && message.trim().length > 0;

  return (
    <div className="error-notification">
      <div className="error-notification__row">
        <button
          type="button"
          className={`error-notification__toggle ${isExpanded ? 'error-notification__toggle--expanded' : ''}`}
          onClick={() => hasDetails && setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
          style={{ cursor: hasDetails ? 'pointer' : 'default' }}
        >
          <span className="error-notification__indicator" />
          <span className="error-notification__summary">Case creation failed</span>
          {hasDetails && (
            <svg className="error-notification__chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          )}
        </button>
        <button
          type="button"
          className="error-notification__dismiss"
          onClick={onDismiss}
          aria-label="Dismiss"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
      {hasDetails && isExpanded && (
        <div className="error-notification__details">
          <div className="error-notification__message">{message}</div>
        </div>
      )}
    </div>
  );
};

/**
 * Case creation form component
 * Handles the form UI for creating a new test case
 */
const CreateCaseForm = ({
  // Form state
  formData,
  customFieldValues,

  // Event handlers
  onChange,
  onCustomFieldChange,
  onSubmit,
  onDismissCaseId,
  onDismissError,

  // Data
  plans,
  isLoadingPlans,
  groupName,
  orgContext,

  // State
  isSubmitting,
  caseId,
  errorMessage,

  // Computed
  requiresPatientDetails,
}) => {
  const env = process.env.REACT_APP_ENV;

  const caseIdBannerStyle = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: '8px',
    padding: '10px 12px',
    margin: '4px 1px 8px 1px',
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: '6px',
  };

  const caseIdContentStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    flex: '1 1 auto',
    minWidth: 0,
  };

  const caseIdLabelStyle = {
    fontSize: '0.75rem',
    fontWeight: 500,
    color: '#16a34a',
  };

  const caseIdValueStyle = {
    fontSize: '0.7rem',
    fontWeight: 500,
    color: '#166534',
    fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
    wordBreak: 'break-all',
  };

  const dismissButtonStyle = {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
    minWidth: '20px',
    padding: 0,
    background: 'transparent',
    border: 'none',
    borderRadius: '4px',
    color: '#16a34a',
    opacity: 0.5,
    cursor: 'pointer',
    transition: 'opacity 0.15s ease',
  };

  return (
    <form onSubmit={onSubmit}>
      {/* Success message with created case ID */}
      {caseId && (
        <div className="success-banner" style={caseIdBannerStyle}>
          <div style={caseIdContentStyle}>
            <span style={caseIdLabelStyle}>Case Created</span>
            <span style={caseIdValueStyle}>{caseId}</span>
          </div>
          <button
            type="button"
            style={dismissButtonStyle}
            onClick={onDismissCaseId}
            aria-label="Dismiss"
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.8'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.5'; }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      )}

      {/* Plan Selection */}
      <div style={{ marginTop: caseId ? 0 : '8px' }}>
        <label>
          Select the outcome plan using the dropdown:
          {isLoadingPlans ? (
            <div className="plan-loading">
              <div className="plan-loading__spinner" />
              <span className="plan-loading__text">Loading plans...</span>
            </div>
          ) : (
            <select
              name="plan"
              value={formData.plan}
              onChange={onChange}
              required
            >
              <option value="">Select a Plan</option>
              {plans.map((plan, index) => (
                <option key={index} value={plan}>
                  {plan}
                </option>
              ))}
            </select>
          )}
        </label>
      </div>

      {/* Custom Plan Fields (if applicable) */}
      {planHasCustomFields(formData.plan, orgContext) && (
        <CustomPlanFields
          config={getPlanFieldConfig(formData.plan, orgContext)}
          values={customFieldValues}
          onChange={onCustomFieldChange}
        />
      )}

      {/* Channel Selection */}
      <div>
        <label>
          Select the channel using the dropdown:
          <select
            name="channel"
            value={formData.channel}
            onChange={onChange}
            required
          >
            <option value="">Select a Communication Channel</option>
            {CHANNELS.map((channel, index) => (
              <option key={index} value={channel}>
                {channel}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* Case Type Selection */}
      <div>
        <label>
          Select the type of case using the dropdown:
          <select
            name="ctype"
            value={formData.ctype}
            onChange={onChange}
            required
          >
            <option value="">Select a Case Type</option>
            {CASE_TYPES.map((ctype, index) => (
              <option key={index} value={ctype}>
                {ctype}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* Patient Fields (conditional based on case type) */}
      {requiresPatientDetails && (
        <PatientFields
          formData={formData}
          onChange={onChange}
        />
      )}

      {/* Phone Number (always required) */}
      <div>
        <label>
          Phone # (10 digits w/o spaces or dashes):
          <input
            type="tel"
            placeholder="1234567890"
            pattern="[0-9]{10}"
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={onChange}
            required
          />
        </label>
      </div>

      {/* Error Message */}
      {errorMessage && (
        <ErrorNotification
          message={errorMessage}
          onDismiss={onDismissError}
        />
      )}

      {/* Submit Button */}
      <button type="submit" disabled={isSubmitting}>
        {isSubmitting
          ? 'Creating...'
          : `${groupName || 'Group'} : Create case in ${env}`}
      </button>
    </form>
  );
};

export default CreateCaseForm;
